import { Divider } from "primereact/divider";
import { Password } from "primereact/password";
import {
  Controller,
  type FieldValues,
  type Path,
  type UseFormReturn,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface PasswordFormData extends FieldValues {
  password: string;
}

type PasswordProps<T extends PasswordFormData> = {
  form: UseFormReturn<T, any, T>;
  required: boolean;
  errorField?: boolean;
  feedback: boolean;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
};

export function PasswordField<T extends PasswordFormData>({
  form,
  required,
  errorField,
  feedback,
  onKeyDown,
}: PasswordProps<T>) {
  const { t } = useTranslation();

  const error = form.formState.errors.password as
    | Record<string, any>
    | undefined;

  return (
    <>
      <Controller
        name={"password" as Path<T>}
        control={form.control}
        rules={{
          required: required && t("Error.Required"),
          minLength: {
            value: 6,
            message: t("Error.MinLength", { value: 6 }),
          },
        }}
        render={({ field }) => (
          <Password
            inputId="password"
            autoComplete="new-password"
            {...field}
            feedback={feedback}
            toggleMask
            className="w100"
            inputClassName="w100"
            invalid={!!error}
            header={<h6>{t("Home.Registration.PasswordHeader")}</h6>}
            footer={
              <>
                <Divider />
                <p className="mt-2">{t("Home.Registration.PasswordRules")}</p>
                <ul
                  className="pl-2 ml-2 mt-0"
                  style={{ lineHeight: "1.5" }}
                >
                  <li>{t("Home.Registration.PasswordRule1")}</li>
                  <li>{t("Home.Registration.PasswordRule2")}</li>
                  <li>{t("Home.Registration.PasswordRule3")}</li>
                  <li>{t("Home.Registration.PasswordRule4")}</li>
                </ul>
              </>
            }
            onKeyDown={onKeyDown}
          />
        )}
      />
      {(errorField ?? true) && error && (
        <small className="p-error block">{error?.message}</small>
      )}
    </>
  );
}
